import React from "react";
import { clsx } from "clsx";
import typographyStyles from "@bloomberg/media-ui-tokens/css/typography.module.css";

import Title from "~components/shared/title/Title";
import {
    type VariationComponent,
    ZoneColumnCombo,
    getModuleSubNavItems,
    getModuleSubNavTitle,
    getModuleSubNavSubtitle,
    getModuleSubNavUrl,
    moduleHasSubNav,
} from "~types/types";

import styles from "./ModuleHeader.module.css";

export const MAX_NAVIGATION_ITEMS = 5;
export const LEFT_CONTENT_WELL_MAX_ITEMS = 3;

interface NavItemProps {
    text: string;
    url: string;
    linkIndex: number;
}

const NavItem = ({ text, url, linkIndex }: NavItemProps) => (
    <a
        data-link-type="Nav"
        data-link-index={linkIndex}
        className={clsx(styles.navItem, typographyStyles.articleLabel)}
        data-component="module-header-nav-item"
        href={url}
    >
        {text}
    </a>
);

export default function ModuleHeader(props: VariationComponent) {
    const {
        title,
        labelUrl,
        subtitle,
        renderingLocation = ZoneColumnCombo.leftContentWell,
    } = props;

    const hasNestedNavigationModule = moduleHasSubNav(props);
    const titleFromNavModule = getModuleSubNavTitle(props);
    const subtitleFromNavModule = getModuleSubNavSubtitle(props);
    const urlFromNavModule = getModuleSubNavUrl(props);
    const navModuleItems = getModuleSubNavItems(props);
    const adjustedMaxItems =
        renderingLocation === ZoneColumnCombo.leftContentWell
            ? LEFT_CONTENT_WELL_MAX_ITEMS
            : MAX_NAVIGATION_ITEMS;

    if (!title && !titleFromNavModule) return null;

    if (!hasNestedNavigationModule) {
        return (
            <div className={styles.ModuleHeader} data-component="module-header">
                <Title
                    title={title}
                    titleUrl={labelUrl}
                    className={clsx(
                        styles.title,
                        typographyStyles.coreHeadlineXxs
                    )}
                />
                {subtitle && (
                    <div
                        className={clsx(
                            styles.subtitle,
                            typographyStyles.articleDek
                        )}
                    >
                        {subtitle}
                    </div>
                )}
            </div>
        );
    }

    return (
        <div className={styles.ModuleHeader} data-component="module-header">
            <div className={styles.titleAndItemsBar}>
                <Title
                    title={titleFromNavModule}
                    className={clsx(
                        styles.title,
                        typographyStyles.coreHeadlineXxs
                    )}
                    titleUrl={urlFromNavModule}
                />
                {navModuleItems
                    .slice(0, adjustedMaxItems)
                    .map(({ headline, url }, index) => (
                        <NavItem
                            linkIndex={index + 1}
                            key={headline}
                            text={headline}
                            url={url}
                        />
                    ))}
            </div>
            {subtitleFromNavModule && (
                <div
                    className={clsx(
                        styles.subtitle,
                        typographyStyles.articleDek
                    )}
                >
                    {subtitleFromNavModule}
                </div>
            )}
        </div>
    );
}
